import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { useCart } from "../lib/CartContext";
import { getIcon } from "../lib/icons";
import { Trash2, Plus, Minus, Truck, Tag, X, AlertCircle } from "lucide-react";
import { useState } from "react";
import CheckoutDialog from "./CheckoutDialog";
import { useLang, localizeEta } from "../lib/i18n";
import api from "../lib/api";
import { toast } from "sonner";

export default function CartSheet() {
  const { items, open, setOpen, checkoutOpen, setCheckoutOpen, updateQty, removeItem, subtotal } = useCart();
  const { t, isRTL, lang } = useLang();
  const [shipping, setShipping] = useState("October");
  const [codeInput, setCodeInput] = useState("");
  const [applied, setApplied] = useState(null); // {code, percent, amount}
  const [applying, setApplying] = useState(false);

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code || subtotal <= 0) return;
    setApplying(true);
    try {
      const { data } = await api.post("/discount/validate", { code, subtotal });
      setApplied(data);
      toast.success(t("cart.discount_applied"));
    } catch (e) {
      setApplied(null);
      toast.error(e.response?.data?.detail || t("cart.discount_invalid"));
    } finally {
      setApplying(false);
    }
  };
  const removeCode = () => { setApplied(null); setCodeInput(""); };

  const discountAmount = applied ? Math.round((subtotal * applied.percent / 100) * 100) / 100 : 0;
  const MIN_ORDER_VALUE = 200;
  const eligibleSubtotal = Math.round((subtotal - discountAmount) * 100) / 100;
  const belowMinimum = items.length > 0 && eligibleSubtotal < MIN_ORDER_VALUE;
  const remainingForMin = Math.max(0, Math.round((MIN_ORDER_VALUE - eligibleSubtotal) * 100) / 100);
  const remainingLabel = remainingForMin % 1 === 0 ? remainingForMin.toFixed(0) : remainingForMin.toFixed(2);

  const SHIPPING = [
    { key: "October", label: t("cart.city_october"), fee: 60, icon: Truck, desc: "30 mins - 2 hours" },
    { key: "Zayed", label: t("cart.city_zayed"), fee: 90, icon: Truck, desc: "1-3 hours" },
    { key: "Beverly Hills", label: t("cart.city_beverly_hills"), fee: 100, icon: Truck, desc: "1-3 hours" },
    { key: "Giza/Harem", label: t("cart.city_giza_harem"), fee: 130, icon: Truck, desc: "2-4 hours" },
  ];

  const shippingFee = SHIPPING.find((s) => s.key === shipping)?.fee || 0;
  const total = subtotal - discountAmount + (items.length ? shippingFee : 0);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side={isRTL ? "left" : "right"}
          className="w-full sm:max-w-md p-0 flex flex-col"
          style={{ background: "#FFFBF2", borderInlineStart: "2px solid #0F172A" }}
          data-testid="cart-sheet"
        >
          <SheetHeader className="p-5 border-b-2 border-slate-900 bg-white">
            <SheetTitle className="font-display text-2xl font-bold">{t("cart.title")}</SheetTitle>
            <SheetDescription className="sr-only">Cart items and shipping selection.</SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-auto p-4">
            {items.length === 0 ? (
              <div className="text-center py-16 text-slate-500" data-testid="cart-empty">
                {t("cart.empty")}
              </div>
            ) : (
              <ul className="flex flex-col gap-3">
                {items.map((it) => {
                  const Icon = getIcon(it.icon);
                  const cartImg = it.image_url ? `${process.env.REACT_APP_BACKEND_URL}${it.image_url}` : null;
                  return (
                    <li key={it.product_id} className="neo-flat p-3 flex gap-3" data-testid={`cart-item-${it.product_id}`}>
                      <div
                        className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
                        style={{ background: it.color, border: "2px solid #0F172A" }}
                      >
                        {cartImg ? (
                          <img src={cartImg} alt={it.name} loading="lazy" className="w-full h-full object-contain" />
                        ) : (
                          <Icon size={28} className="text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold leading-tight truncate">{it.name}</div>
                        <div className="text-sm text-slate-600">{it.price.toFixed(0)} EGP</div>
                        <div className="mt-2 inline-flex items-center neo-flat">
                          <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-2 py-1" data-testid={`cart-dec-${it.product_id}`}><Minus size={14} /></button>
                          <span className="px-2 text-sm font-semibold">{it.quantity}</span>
                          <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="px-2 py-1" data-testid={`cart-inc-${it.product_id}`}><Plus size={14} /></button>
                        </div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeItem(it.product_id)}
                          className="text-slate-500 hover:text-red-500"
                          data-testid={`cart-remove-${it.product_id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                        <div className="font-bold">{(it.price * it.quantity).toFixed(0)} EGP</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {items.length > 0 && (
              <div className="mt-5">
                <div className="font-display text-sm font-semibold mb-2">{t("cart.shipping")}</div>
                <div className="grid grid-cols-1 gap-2">
                  {SHIPPING.map((s) => {
                    const Icon = s.icon;
                    const active = shipping === s.key;
                    return (
                      <button
                        key={s.key}
                        onClick={() => setShipping(s.key)}
                        data-testid={`shipping-${s.key.toLowerCase().replace(/[\s/]+/g, "-")}`}
                        className="neo-flat p-3 flex items-center gap-3 text-start"
                        style={active ? { background: "#FDE047" } : { background: "#fff" }}
                      >
                        <Icon size={20} />
                        <div className="flex-1">
                          <div className="font-semibold">{s.label}</div>
                          <div className="text-xs text-slate-600">{localizeEta(s.desc, lang)}</div>
                        </div>
                        <div className="font-bold">{s.fee} EGP</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t-2 border-slate-900 bg-white p-4 space-y-2">
              <div className="pb-2 border-b border-dashed border-slate-300">
                {!applied ? (
                  <div className="flex gap-2 items-center" data-testid="discount-row">
                    <Tag size={16} className="shrink-0" />
                    <input
                      type="text"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      placeholder={t("cart.discount_code")}
                      data-testid="discount-code-input"
                      className="flex-1 neo-flat px-2 py-1.5 text-sm"
                      dir="ltr"
                    />
                    <button
                      type="button"
                      onClick={applyCode}
                      disabled={applying || !codeInput.trim()}
                      className="neo-btn px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
                      style={{ background: "var(--pm-dog)" }}
                      data-testid="discount-apply-btn"
                    >
                      {t("cart.apply")}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between text-sm" data-testid="discount-applied">
                    <span className="font-bold" style={{ color: "#059669" }}>
                      {applied.code} — {applied.percent}% OFF
                    </span>
                    <button
                      type="button"
                      onClick={removeCode}
                      className="text-xs font-semibold underline inline-flex items-center gap-1"
                      data-testid="discount-remove-btn"
                    >
                      <X size={12} /> {t("cart.remove_code")}
                    </button>
                  </div>
                )}
              </div>
              <Row label={t("cart.subtotal")} value={`${subtotal.toFixed(0)} EGP`} />
              {applied && <Row label={`${t("cart.discount")} (${applied.percent}%)`} value={`- ${discountAmount.toFixed(0)} EGP`} />}
              <Row label={t("cart.ship_label")} value={`${shippingFee} EGP`} />
              <div className="flex justify-between items-center pt-2 border-t border-dashed border-slate-300">
                <span className="font-display text-lg font-bold">{t("cart.total")}</span>
                <span className="font-display text-2xl font-bold" data-testid="cart-total">{total.toFixed(0)} EGP</span>
              </div>
              {belowMinimum && (
                <div
                  className="flex items-start gap-2 text-sm font-semibold px-3 py-2 rounded-lg"
                  style={{ background: "#FEF3C7", border: "2px solid #0F172A", color: "#92400E" }}
                  data-testid="min-order-message"
                >
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{t("cart.min_order_message", { amount: remainingLabel })}</span>
                </div>
              )}
              <button
                onClick={() => setCheckoutOpen(true)}
                disabled={belowMinimum}
                className="neo-btn w-full py-3 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "var(--pm-dog)" }}
                data-testid="checkout-btn"
              >
                {t("cart.checkout")}
              </button>
              <p className="text-xs text-slate-500 text-center">{t("cart.note")}</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
      <CheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        shippingMethod={shipping}
        shippingFee={shippingFee}
        discountCode={applied ? applied.code : ""}
        discountPercent={applied ? applied.percent : 0}
        discountAmount={discountAmount}
      />
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );
}
